/**
 * Labels a Sentry event with WHICH third party it came from, as the `error_source` tag.
 *
 * Roughly three quarters of what reaches the orion-web Sentry project is not orion-web, and the
 * dozen issues that are ours sit under it. Wix's Sentry Best Practices Guide's answer to that is
 * `thirdPartyErrorFilterIntegration`, which is what instrumentation-client.ts uses: the bundler
 * plugin stamps our modules with the `orion-web` application key, the integration compares every
 * frame against it, and an event made exclusively of frames we did not ship gets
 * `third_party_code: true`. That is provenance, not a guess at filenames, so it does not rot when
 * MetaMask renames `inpage.js`.
 *
 * Five things that integration structurally cannot see, which this adds:
 *
 *   1. **An ad blocker refusing a beacon.** `TypeError: Failed to fetch` for px.ads.linkedin.com
 *      bottoms out in OUR fetch wrapper, so its frames are ours and it is correctly not
 *      third-party — but it is also not a bug. 1670 events in 30 days, the second-largest source
 *      in the project. Only the host it was reaching for tells them apart, and that lives in a
 *      breadcrumb, not a frame.
 *   2. **WHICH third party.** `third_party_code` is one bit. Wix's own platform executor throwing
 *      ~70×/day next to duplexer.wix.com traffic is a question for the platform team; a visitor's
 *      wallet extension is not. Collapsing those two into one bit loses the only one worth acting
 *      on.
 *   3. **A vendor we BUNDLE.** `@wix/duplexer-js` ships through npm into our own chunk, so it
 *      carries our application key and is third-party to nobody's provenance check — while being
 *      responsible for ~1900 events a month from one defect of its own. See
 *      `DUPLEXER_FRAME_FUNCTIONS`.
 *   4. **An event that cannot be triaged at all.** A promise rejected with a bare DOM event has no
 *      stack, no message and no owner; provenance has nothing to read. See `NON_ERROR_REJECTION`.
 *   5. **A stack that is entirely OURS and still somebody else's rejection.** An injected script
 *      that wraps `window.fetch` and taps the promise without a catch produces an unhandled
 *      rejection whose every frame we shipped, and whose every frame handled it. Provenance is
 *      not wrong here, it is answering a different question. See `FETCH_INTERCEPTOR_FILE`.
 *   6. **A host bridge's frameless refusal.** An in-app WebView's native bridge rejecting a call
 *      it does not implement carries no frames at all, so provenance has nothing to compare. See
 *      `NATIVE_BRIDGE_REFUSAL`.
 *
 * It TAGS and does not drop. `beforeSend` never returns null and the integration runs in
 * apply-tag mode, because a dropped event cannot be reconsidered: if a rule here is wrong the
 * events are still in the project, still grouped, still graphed, and the only thing that changed
 * is which chip you click. At ~5.5k events/month there is no quota that dropping would buy
 * anything from. For the same reason `app` is the default — anything unrecognised stays ours and
 * stays in the triage view.
 *
 * Triage view: `is:unresolved error_source:app`.
 */
export type ErrorSource =
  | 'app'
  /** Wix's own platform code running in our page — the script executor, and bundled duplexer-js. */
  | 'wix-platform'
  /** A beacon an ad blocker refused. */
  | 'analytics'
  /** An extension, an injected script, an in-app browser's instrumentation. */
  | 'third-party'
  /** A frameless non-Error rejection: nothing to act on, whoever it belongs to. */
  | 'non-error';

/** The shape this reads out of a Sentry event. Structural so the classifier is testable alone. */
export interface ClassifiableEvent {
  tags?: Record<string, unknown>;
  exception?: {
    values?: Array<{
      type?: string;
      value?: string;
      mechanism?: { type?: string };
      stacktrace?: { frames?: Array<{ filename?: string; function?: string }> };
    }>;
  };
  breadcrumbs?: Array<{ category?: string; level?: string; data?: { url?: string } }>;
}

/**
 * Wix's platform script executor. Its unhandled rejections land on us because they run in our
 * page — 2176 in 30 days, all reading 'M_ID', all next to duplexer.wix.com XHRs. Named separately
 * from the rest of `third_party_code` because if Duplexer is failing for our users, this is the
 * only signal we get of it: it wants an owner, not a mute.
 */
const WIX_PLATFORM_FILE = /\/executors\/[^/]*\.js$/;

/**
 * `@wix/duplexer-js`, which is Wix platform code that happens to arrive as an npm dependency.
 *
 * It accounts for ~1900 events a month on its own, in four groups, all one defect:
 * `Transport.close()` called with no argument passes `undefined` to `onTransportClose`, which
 * re-emits it as `connect_error`, and both `strategy.js` and `transports-manager.js` then do
 * `if (error.recoverable)`. (`connection-manager.js` optional-chains the very same read, so the
 * library already knows.) It fires from inside a rejection handler, so nothing user-facing breaks
 * — it is pure noise, and it is noise we cannot fix from this repo: 1.0.822 is the latest version.
 *
 * **Matched on FUNCTION names, not filenames, and that is not a shortcut.** This classifier runs in
 * `beforeSend`, in the browser, where duplexer's frames are still our own bundle
 * (`app:///_next/static/chunks/3990.<hash>.js`) — the `node_modules/@wix/duplexer-js/...` paths
 * only exist after Sentry symbolicates, server-side, long after we have had our say. What DOES
 * survive minification is a prototype method's name, because it is a property key
 * (`TransportsManager.prototype.abortStrategiesWithNoOpenTransport = ...`), which is why these read
 * back off a production stack verbatim. Each name below is one duplexer's transport layer owns and
 * nothing in orion-web has.
 *
 * Tagged `wix-platform` rather than `third-party` for the same reason `executors/200.js` is: if
 * Duplexer is failing for our users this is the only signal we get of it, so it wants an owner.
 */
const DUPLEXER_FRAME_FUNCTIONS = new Set([
  'abortStrategiesWithNoOpenTransport',
  'connectToFirstToRespondStrategy',
  'listenAndEmitConnectErrorsInAllTransportsOnConnecting',
  'listenEventsOfActiveTransport',
  'connectWithBackoff',
  'onTransportClose',
]);

/**
 * Beacon hosts whose refusal by an ad blocker is not a bug of ours. Suffix-matched, so an entry
 * also covers its subdomains.
 */
const ANALYTICS_HOSTS = [
  'px.ads.linkedin.com',
  'analytics.google.com',
  'region1.analytics.google.com',
  'www.google-analytics.com',
  'ad.doubleclick.net',
  'googleads.g.doubleclick.net',
  'stats.g.doubleclick.net',
  'server-side-tagging-oh37ak3tfq-uc.a.run.app',
  'ct.pinterest.com',
];

/**
 * Beacon hosts matched EXACTLY, subdomains excluded.
 *
 * `gtag.js` posts Google Ads conversions to `google.com/pagead/…` and `www.google.com/ccm/collect`
 * — ~110 events a month, every one an ad blocker — so the bare apex has to be on the list. It
 * cannot go on the suffix list above: `.google.com` would then also swallow `accounts.google.com`
 * and `calendar.google.com`, and a failing fetch to those is the Google Calendar connect flow
 * breaking, which is the opposite of noise (docs/claude/google-calendar-oauth.md).
 */
const ANALYTICS_HOSTS_EXACT = ['google.com', 'www.google.com'];

/**
 * An in-app browser's own injected instrumentation — Meta's, on both phones.
 *
 * Instagram/Facebook rewrite the page they host and run their own script in it: a login-field
 * logger, a navigation-performance logger, a postMessage bridge to the native shell. When that
 * script throws (`Java object is gone` once the WebView's bridge is torn down, an
 * `InvalidAccessError` from its MutationObserver), the error lands in OUR project, ~160 events a
 * month between the two.
 *
 * `thirdPartyErrorFilterIntegration` catches the iOS one and NOT the Android one, because the
 * Android stack passes through a `@sentry/browser` helper frame that we shipped — so the event is
 * not made *exclusively* of third-party frames and stays `app`. These names close that gap.
 *
 * Deliberately keyed on the injected script, never on `browser.name`: a genuine orion-web bug hit
 * by someone who opened a link inside Instagram is still our bug.
 */
const IN_APP_BROWSER_FILE = /navigation_performance_logger/;
const IN_APP_BROWSER_FUNCTIONS = new Set([
  'logLoginFieldDetected',
  'dispatchToBridge',
  'sendDataToNative',
  'sendJsBlockingTimeMessage',
]);

/**
 * An injected script that has wrapped `window.fetch` and taps the promise WITHOUT a catch.
 *
 * This one is worth reading twice, because the stack lies about who is at fault. A representative
 * event — 2881 of them on one issue — reads top to bottom as pure orion-web:
 *
 *     ./lib/duplexer-connection.ts:178  getInstanceForDuplexer
 *     ./lib/duplexer-connection.ts:163  mintInstance
 *     ./lib/duplexer-connection.ts:148  runMint
 *     ./lib/duplexer-connection.ts:48   fetchInstance
 *     ./lib/install-next-data-mount-prefix.ts:53  window.fetch
 *     app:///frame_ant/frame_ant.js:2   window.fetch     ← not ours
 *
 * Every one of our frames in that chain handles the rejection: `fetchInstance` awaits the fetch in
 * a `try`, converts it to an `InstanceError`, and `getInstanceForDuplexer` catches THAT and returns
 * from cache. Nothing of ours rethrows the raw `TypeError`. The reason it still arrives as an
 * `onunhandledrejection` is that the interceptor took its own branch off the same promise — the
 * classic `const p = orig(...); p.then(report); return p;` — and that sibling has no handler. The
 * frames are the async CALL chain at construction time, not the path the rejection escaped by.
 *
 * So a stack can be entirely ours and the unhandled rejection still be somebody else's, which is
 * exactly the case provenance filtering cannot see: our frames dominate, so the event is not
 * *exclusively* third-party and stays `app`.
 *
 * `frame_ant/frame_ant.js` is not served by us — `symphony.wix.com/frame_ant/frame_ant.js` 404s to
 * our own Next error page. It is injected with a page-relative src, which is why Sentry normalises
 * it under `app:///` and it reads like one of our assets.
 *
 * ⚠️ **The cost of this rule, stated plainly:** in a tab carrying such an interceptor, ANY failing
 * fetch produces one of these — including a genuine outage of one of our own endpoints. That is
 * acceptable only because this TAGS and never drops, and because an endpoint of ours going down is
 * something we learn from our own error paths and server-side metrics, never from an injected
 * script's unhandled rejection. If this rule ever starts hiding something, the events are still in
 * the project under `error_source:third-party`.
 *
 * `frame_ant` is not the only one. Two more wrappers produce the identical shape, and between them
 * they were most of the merged offline-read issue (7677349086, every recent event carrying one):
 * `app:///js/injected/hook.js` (`Object.apply`) and `app:///injectScriptAdjust.js`. Both 404 on
 * symphony.wix.com exactly as `frame_ant.js` does. Matched on the WHOLE path, never on a basename —
 * `hook.js` alone is a name a chunk of ours could one day carry.
 *
 * `app:///js/mainWorld.js` is a fourth, an extension's main-world content script (ORION-WEB-5A,
 * ORION-WEB-9A): the innermost frame under duplexer's `connectWithBackoff` / `poll` and our own
 * `window.fetch`, on one visitor's /settings tab. It 404s on symphony.wix.com too.
 */
const FETCH_INTERCEPTOR_FILE =
  /\/frame_ant\/|^app:\/\/\/js\/injected\/hook\.js$|^app:\/\/\/injectScriptAdjust\.js$|^app:\/\/\/js\/mainWorld\.js$/;

/**
 * Sentry's own wording for a promise rejected with something that is not an Error and carries no
 * stack: `Event \`CustomEvent\` (type=unhandledrejection) captured as promise rejection`. The
 * CustomEvent / ProgressEvent / XMLHttpRequestProgressEvent groups are ~170 events a month of
 * exactly this — no stack, no message, no route beyond the transaction, nothing to act on even in
 * principle, and (on the evidence of every one sampled) an injected script's event loop rather
 * than ours.
 *
 * Its own source rather than `third-party`, because we do not KNOW whose it is; what we know is
 * that it cannot be triaged. A frameless non-Error is the one event shape where staying in the app
 * view costs a reader attention and can never repay it.
 */
const NON_ERROR_REJECTION = /^Event `[^`]+` \(type=[^)]*\) captured as promise rejection/;

/**
 * An in-app WebView's native bridge refusing a call it has no handler for:
 * `InvalidParametersError: user:action getUserId not found` (7774600670). That is a
 * `<namespace>:action <name> not found` RPC router, and nothing in orion-web or its
 * dependencies has one. No `InvalidParametersError`, no `getUserId`, no `:action` dispatch
 * anywhere in the bundle. It arrived with no frames, on an iPhone whose user agent claims Mobile
 * Safari 27 on iOS 18.7, which is a WebView spoofing Safari, next to a page full of
 * GTM-loaded pixels probing for a host app.
 *
 * Keyed on the error's class AND the router's wording AND the absence of frames, so all three
 * have to line up. One of ours with a stack under it, or one that merely says "not found", stays
 * `app`.
 */
const NATIVE_BRIDGE_ERROR_TYPE = 'InvalidParametersError';
const NATIVE_BRIDGE_REFUSAL = /^[\w-]+:action \S+ not found$/;

/**
 * A promise rejected with a plain object and no stack: `Object captured as promise rejection with
 * keys: code, message, status`. orion-web never rejects with a plain object — there is no
 * `reject({` or `throw {` anywhere in it — and every one sampled belonged to someone else: a
 * Chrome sidebar extension's `TOKEN_EXPIRED` refresh (ORION-WEB-X), WalkMe's own
 * `userMapping` timeout (ORION-WEB-7V), a WebView bridge's `sentMessage` refusal (ORION-WEB-6S).
 * `non-error` rather than `third-party` for the same reason as `NON_ERROR_REJECTION`: what we know
 * is that it cannot be triaged, not whose it is.
 */
const PLAIN_OBJECT_REJECTION = /^Object captured as promise rejection with keys: /;

/**
 * The visitor's disk is full. `QuotaExceededError` and IndexedDB's `FILE_ERROR_NO_SPACE` arrive
 * frameless out of Firebase Analytics' own IndexedDB setup (ORION-WEB-27, ORION-WEB-4H), which we
 * cannot catch. Frameless only: OUR `localStorage` writes throw synchronously, with a stack, and
 * one of those that escapes is a bug of ours.
 *
 * The DOMException can also arrive re-wrapped as a plain `Error` whose message is just its name
 * (ORION-WEB-95: `Error: QuotaExceededError`, `DOMException.code: 22`, no frames), so the message
 * is matched for that whole name too.
 */
const DISK_FULL_TYPE = 'QuotaExceededError';
const DISK_FULL_MESSAGE = /FILE_ERROR_NO_SPACE|^QuotaExceededError$/;

/**
 * An abort or timeout that reached `onunhandledrejection`. Every abort orion-web causes — React
 * Query cancelling a query, `use-chat-history` unmounting, the flag fetches' `AbortSignal.timeout`
 * — is caught by its caller, and a clean session aborting each of them produces nothing. What
 * still arrives (ORION-WEB-20/6B/7N/76/7P/3Y, ~50 events a month, 30 of them from one visitor) is
 * the `FETCH_INTERCEPTOR_FILE` shape WITHOUT the interceptor's frame to match on: an injected
 * `window.fetch` wrapper tapping the promise with no catch, the stack showing only where the abort
 * was triggered. An inference, not a sighting — the wrapper's source was not readable — and the
 * rule is safe either way: an abort is something we asked for, so one of ours left unhandled
 * would still be harmless.
 */
const ABORT_TYPE = /^(AbortError|TimeoutError)$/;
const ABORT_MESSAGE = /^(signal is aborted without reason|signal timed out|The operation was aborted\.?)$/;
const UNHANDLED_REJECTION_MECHANISM = /onunhandledrejection$/;

/**
 * A frame out of a script inline in the HTML document: Sentry names it after the page itself
 * (`app:///`, `app:///workspace/hero-digital`), with no file extension. orion-web ships no inline
 * scripts — every `<script>` in `_document.tsx` has a `src` — so code running there was put there
 * by GTM, an extension or a host app. ORION-WEB-16, a cross-origin `SecurityError` from an
 * `initialLoad` we do not have, stayed `app` only because a Sentry timer-wrapper frame of ours
 * was beneath it.
 */
const INLINE_DOCUMENT_SCRIPT = /^app:\/\/\/[^.]*$/;

/**
 * A request that never went out. An ad blocker produces exactly this for a beacon, and so does a
 * real outage of one of OUR endpoints — which is why the host has to match before it counts.
 */
const NETWORK_FAILURE =
  /^(Failed to fetch|Load failed|NetworkError when attempting to fetch resource\.?)$/;

/**
 * The synchronous-XHR wording of the same refusal, which names the URL itself rather than a host in
 * parentheses: `Failed to execute 'send' on 'XMLHttpRequest': Failed to load 'https://ct.pinterest.com/stats/'.`
 * Pinterest's `ct/core.js` sends its beacon this way (7719472573).
 */
const XHR_SEND_FAILURE = /^Failed to execute 'send' on 'XMLHttpRequest': Failed to load '([^']+)'/;

/** Paths only a chunk WE built is served from. */
const OWN_BUNDLE_FILE = /\/_next\/(static\/chunks|server)\//;

/**
 * Strips the query string and fragment off a frame's filename before it is matched. A script the
 * page loads is routinely cache-busted (`gtm.js?id=…`, `200.js?v=…`), and a suffix match against
 * the raw filename silently misses every one of those.
 */
const pathOf = (filename: string): string => filename.split(/[?#]/)[0] ?? '';

const hostOf = (url: string): string => {
  try {
    return new URL(url).hostname;
  } catch {
    return '';
  }
};

const isAnalyticsHost = (host: string): boolean =>
  host !== '' &&
  (ANALYTICS_HOSTS.some((h) => host === h || host.endsWith(`.${h}`)) ||
    ANALYTICS_HOSTS_EXACT.includes(host));

/** A frame's own name, with the minified class/object prefix (`e.`, `Object.`) taken off. */
const methodOf = (fn: string): string => fn.split('.').pop() ?? '';

export function classifyErrorSource(event: ClassifiableEvent): ErrorSource {
  const exception = event.exception?.values?.[event.exception.values.length - 1];
  const frames = exception?.stacktrace?.frames ?? [];
  const message = exception?.value ?? '';

  // A bare network failure has no frame worth reading — every one bottoms out in our fetch
  // wrapper. The host is in the message (`Failed to fetch (host)`) or the last request breadcrumb.
  const isNetworkFailure = NETWORK_FAILURE.test(message.replace(/\s*\([^)]*\)\s*$/, '').trim());
  if (isNetworkFailure) {
    const parenthesised = /\(([^)]+)\)\s*$/.exec(message)?.[1] ?? '';
    if (isAnalyticsHost(parenthesised)) return 'analytics';

    // The request that FAILED, when a breadcrumb says so: a beacon fired a moment after our own
    // request died would otherwise be read as the culprit (ORION-WEB-7W — our `/api/client-error`
    // failing, a googleads beacon after it, and the event tagged `analytics`).
    const requests = (event.breadcrumbs ?? []).filter(
      (b) => b.category === 'fetch' || b.category === 'xhr',
    );
    const failed = requests.filter((b) => b.level === 'error');
    const culprit = failed.length > 0 ? failed : requests;
    const lastUrl = culprit[culprit.length - 1]?.data?.url ?? '';
    if (isAnalyticsHost(hostOf(lastUrl))) return 'analytics';
  }
  const xhrUrl = XHR_SEND_FAILURE.exec(message)?.[1];
  if (xhrUrl && isAnalyticsHost(hostOf(xhrUrl))) return 'analytics';

  if (frames.some((f) => WIX_PLATFORM_FILE.test(pathOf(f.filename ?? '')))) return 'wix-platform';

  // A duplexer frame means the error was raised INSIDE duplexer. A network failure that merely
  // propagated through one is a different thing entirely and stays ours: duplexer asks us for the
  // instance, so `Failed to fetch` under `connectWithBackoff` is OUR `/api/auth/duplexer-instance`
  // being unreachable — the loudest issue in the project for six weeks, and a real bug both times
  // it appeared. Tagging that away would hide the next one.
  //
  // One refinement on that, below: the same shape WITH a fetch-interceptor frame is the
  // interceptor's uncaught tap rather than our endpoint, because our own frames in that chain all
  // handle it. `FETCH_INTERCEPTOR_FILE` has the evidence.
  if (
    !isNetworkFailure &&
    frames.some((f) => DUPLEXER_FRAME_FUNCTIONS.has(methodOf(f.function ?? '')))
  ) {
    return 'wix-platform';
  }

  if (
    frames.some(
      (f) =>
        IN_APP_BROWSER_FILE.test(pathOf(f.filename ?? '')) ||
        FETCH_INTERCEPTOR_FILE.test(pathOf(f.filename ?? '')) ||
        IN_APP_BROWSER_FUNCTIONS.has(methodOf(f.function ?? '')) ||
        INLINE_DOCUMENT_SCRIPT.test(pathOf(f.filename ?? '')),
    )
  ) {
    return 'third-party';
  }

  if (
    UNHANDLED_REJECTION_MECHANISM.test(exception?.mechanism?.type ?? '') &&
    (ABORT_TYPE.test(exception?.type ?? '') || ABORT_MESSAGE.test(message))
  ) {
    return 'third-party';
  }

  // Last of the specific rules, and only when there is genuinely nothing under it: a non-Error
  // rejection that DOES carry frames has somewhere to look, so it stays in the app view.
  if (frames.length === 0 && NON_ERROR_REJECTION.test(message)) return 'non-error';
  if (frames.length === 0 && PLAIN_OBJECT_REJECTION.test(message)) return 'non-error';
  if (
    frames.length === 0 &&
    (exception?.type === DISK_FULL_TYPE || DISK_FULL_MESSAGE.test(message))
  ) {
    return 'non-error';
  }

  if (
    frames.length === 0 &&
    exception?.type === NATIVE_BRIDGE_ERROR_TYPE &&
    NATIVE_BRIDGE_REFUSAL.test(message)
  ) {
    return 'third-party';
  }

  // The backstop next.config.js's `applicationKey` comment points at. If the key ever stops being
  // injected, every frame reads as third-party and the integration tags our OWN errors — which the
  // triage view would then hide, the one outcome this whole setup exists to avoid. A frame served
  // out of our own chunk directory is proof enough to override it.
  // Known gap, deliberate: `…exclusively…` does not tag a MIXED stack, so the ~40 events a month
  // where an injected script fails underneath one of our frames (/settings/connectors) stay `app`.
  // That is the honest place for them — they reach Sentry at all because our fetch wrapper
  // re-throws somebody else's failure bare, and tagging them away would delete the evidence for
  // fixing the wrapper, which is what actually stops them.
  if (event.tags?.third_party_code === true || event.tags?.third_party_code === 'true') {
    const looksLikeOurs = frames.some((f) => OWN_BUNDLE_FILE.test(pathOf(f.filename ?? '')));
    return looksLikeOurs ? 'app' : 'third-party';
  }

  return 'app';
}
